import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import apiClient from "../services/api/client";

const COMPANY_DOMAINS = ["@trailblazerops.com", "@duneworks.ai"];

interface SubscriptionGate {
  /** null = still loading, true = full access, false = redirect to pricing */
  accessGranted: boolean | null;
  isLoading: boolean;
  planName: string | null;
  isFree: boolean;
}

/**
 * Replicates the extension's `checkUser` flow for the web app.
 *
 * 1. Company emails (@duneworks.ai, @trailblazerops.com) → instant access.
 * 2. External users → GET /api/v1/subscription/status
 *    - hasActiveSubscription && !isFree → access granted
 *    - hasActiveSubscription && isFree  → access denied (→ /pricing)
 *    - !hasActiveSubscription           → access granted (trial/beta)
 * 3. Retries up to 3× on auth errors (token race condition after login).
 */
export function useSubscriptionGate(): SubscriptionGate {
  const { user, isAuthenticated } = useAuth();
  const [accessGranted, setAccessGranted] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [planName, setPlanName] = useState<string | null>(null);
  const [isFree, setIsFree] = useState(false);

  const isCompanyUser = useCallback((email: string): boolean => {
    const domain = email.substring(email.indexOf("@"));
    return COMPANY_DOMAINS.includes(domain);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setIsLoading(false);
      return;
    }

    // Company users get instant access
    const isCompany = isCompanyUser(user.email);
    if (isCompany) {
      setAccessGranted(true);
      setIsLoading(false);
    }

    // User explicitly skipped pricing this session → grant access but still
    // fetch plan info so isFree / planName are populated.
    const skipped = sessionStorage.getItem("user_subscribed") === "true";
    if (skipped) {
      setAccessGranted(true);
      setIsLoading(false);
    }

    let cancelled = false;

    const checkSubscription = async (retryCount = 0) => {
      try {
        const response = await apiClient.get("/api/v1/subscription/status");
        if (cancelled) return;

        const rawData = response?.data?.data || response?.data || response;

        if (!rawData || typeof rawData.hasActiveSubscription === "undefined") {
          // Check if it's an auth error worth retrying
          const errorMsg = rawData?.error || rawData?.message || "";
          if (
            typeof errorMsg === "string" &&
            errorMsg.toLowerCase().includes("authentication") &&
            retryCount < 3
          ) {
            setTimeout(() => checkSubscription(retryCount + 1), 500);
            return;
          }
          if (!skipped && !isCompany) setAccessGranted(false);
          setIsLoading(false);
          return;
        }

        const subscriptionData = rawData;
        const plan = subscriptionData.plan;
        setPlanName(plan?.name || null);

        if (subscriptionData.hasActiveSubscription === true) {
          if (plan?.isFree === true) {
            setIsFree(true);
          }
          setAccessGranted(true);
        } else {
          // No active subscription — grant access but show upgrade button
          setIsFree(true);
          setAccessGranted(true);
        }
      } catch (error: unknown) {
        if (cancelled) return;
        // Retry on network/auth errors
        if (retryCount < 3) {
          setTimeout(() => checkSubscription(retryCount + 1), 500);
          return;
        }
        // After retries, grant access to avoid blocking users on transient errors
        setAccessGranted(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    checkSubscription();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user, isCompanyUser]);

  return { accessGranted, isLoading, planName, isFree };
}
